import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const CommunityCalendar = () => {
  const monthlyThemes = [
    { month: 'April 2025', type: 'Western Universe', name: 'Justice League', tagline: '"The world needs its heroes. So do we." A month of justice, power, and what it truly means to protect.', category: 'western' },
    { month: 'May 2025', type: 'Anime Universe', name: 'Demon Slayer: Breath of the Blade', tagline: '"Every scar tells a story." A month of will, sacrifice, and the flame that never dies.', category: 'anime' },
    { month: 'June 2025', type: 'Western Universe', name: 'Arcane', tagline: '"Two sides of the same broken city." A month of duality, ambition, and the cost of progress.', category: 'western' },
    { month: 'July 2025', type: 'Anime Universe', name: 'Attack on Titan: The Last War', tagline: '"Freedom was always the lie we told ourselves." A month of truth, ruin, and what survives.', category: 'anime' },
    { month: 'August 2025', type: 'Western Universe', name: 'Spider-Man: The Spider-Verse', tagline: '"Anyone can wear the mask." A month of identity, courage, and the power of the ordinary.', category: 'western' },
    { month: 'September 2025', type: 'Anime Universe', name: 'Jujutsu Kaisen: Cursed Domains', tagline: '"You don\'t choose the curse. The curse chooses you." A month of strength born in darkness.', category: 'anime' },
    { month: 'October 2025', type: 'Western Universe', name: 'Batman: The Dark Vigil', tagline: '"Fear can be a weapon or a prison." A month of shadows, obsession, and what drives a soul to the edge.', category: 'western' },
    { month: 'November 2025', type: 'Anime Universe', name: 'One Piece: The Grand Voyage', tagline: '"The greatest treasure was never gold." A month of dreams, brotherhood, and the sea that never ends.', category: 'anime' },
    { month: 'December 2025', type: 'Western Universe — Grand Finale', name: 'Avengers: The Infinite Saga', tagline: '"Whatever it takes." The year closes with the grandest assembly — a celebration of all that was built together.', category: 'western' },
  ];

  const communityEvents = [
    { month: 'April 2025 — Community Launch', name: '⚔️ The Gathering — Grand Community Opening', desc: 'The official launch of the ANIMAC METAVERSE community structure. All gates open, all groups introduced.', tag: 'Grand Event', tagClass: 'tag-grand' },
    { month: 'May 2025', name: '🎴 The East-West Clash — Vol. I', desc: 'East Gate vs West Gate in a community-wide debate: Anime vs Western storytelling.', tag: 'Community Event', tagClass: 'tag-community' },
    { month: 'June 2025', name: '📖 Meta Library Grand Opening — Reading Week', desc: 'A full week dedicated to the Meta Library. Daily spotlight drops and reading recommendations.', tag: 'Special Event', tagClass: 'tag-special' },
    { month: 'July 2025', name: '🌌 Mid-Year Anniversary Celebration', desc: 'Three months in — the community reflects, celebrates members, and announces mid-year MVPs.', tag: 'Grand Event', tagClass: 'tag-grand' },
    { month: 'August 2025', name: '🎭 Animac Metaverse Cosplay & Fan Art Showcase', desc: 'The community\'s first creative showcase. Members submit fan art, cosplay photos, and creative work.', tag: 'Special Event', tagClass: 'tag-special' },
    { month: 'September 2025', name: '⚡ The Ragnarök Community Tournament — Season I', desc: 'The community\'s first open tournament. Trivia battles, debate duels, and knowledge challenges.', tag: 'Grand Event', tagClass: 'tag-grand' },
    { month: 'October 2025 — Dark Season', name: '🕷️ Tartarus Night — The Dark Convergence', desc: 'Tartarus opens its gates to the whole community for one night. Dark-themed content and chilling discussions.', tag: 'Grand Event', tagClass: 'tag-grand' },
    { month: 'November 2025', name: '🏴‍☠️ The Grand Voyage Fest — One Piece Community Week', desc: 'A full week under the One Piece theme. Daily episode marathons, lore discussions, and crew-building activities.', tag: 'Special Event', tagClass: 'tag-special' },
    { month: 'December 2025 — Year End', name: '🌟 Animac Metaverse Year-End Gala', desc: 'The grandest event of the year. Community-wide wrap-up, annual awards, and the unveiling of 2026 themes.', tag: 'Grand Finale', tagClass: 'tag-grand' },
  ];

  const groups = [
    {
      name: 'East Gate',
      subtitle: 'Eastern Content & Culture',
      icon: '🏯',
      category: 'east',
      officials: [
        { role: 'Gate Warden', name: '[ Name ]' },
        { role: 'East Curator', name: '[ Name ]' },
        { role: 'Lore Keeper', name: '[ Name ]' },
        { role: 'Community Liaison', name: '[ Name ]' },
      ],
      events: [
        { month: 'April', name: '🌸 Spring Anime Season Kickoff', desc: 'East Gate officially previews the new anime season.' },
        { month: 'June', name: '🎌 Eastern Culture Deep Dive', desc: 'Exploring the cultural roots behind popular anime.' },
        { month: 'August', name: '🏆 Best Anime of the Year — East Vote', desc: 'Members nominate and vote on the best anime releases.' },
        { month: 'October', name: '👹 Halloween Anime Special', desc: 'Dark anime marathon week with curated picks.' },
        { month: 'December', name: '🌟 East Gate Year-End Awards', desc: 'Best anime, best character, best moment.' },
      ],
    },
    {
      name: 'West Gate',
      subtitle: 'Western Content & Cinema',
      icon: '🎬',
      category: 'west',
      officials: [
        { role: 'Gate Warden', name: '[ Name ]' },
        { role: 'Cinema Director', name: '[ Name ]' },
        { role: 'Series Curator', name: '[ Name ]' },
        { role: 'Community Liaison', name: '[ Name ]' },
      ],
      events: [
        { month: 'April', name: '⚡ Justice League Opening Watch Party', desc: 'Community watch party with live reactions.' },
        { month: 'July', name: '🎥 Summer Blockbuster Breakdown', desc: 'Reviews and debates on summer releases.' },
        { month: 'September', name: '📺 Series Spotlight Week', desc: 'Deep lore dives and character analysis.' },
        { month: 'November', name: '🍂 Pre-Awards Season Picks', desc: 'Predicting the year\'s best western films.' },
        { month: 'December', name: '🌟 West Gate Year-End Awards', desc: 'Best film, best series, best performance.' },
      ],
    },
    {
      name: 'Meme Philosophy',
      subtitle: 'The Art of Laughing at Existence',
      icon: '🧠',
      category: 'meme',
      officials: [
        { role: 'Chief Philosopher', name: '[ Name ]' },
        { role: 'Meme Archivist', name: '[ Name ]' },
        { role: 'Chaos Moderator', name: '[ Name ]' },
      ],
      events: [
        { month: 'April', name: '🃏 Meme Olympics — Round I', desc: 'Members submit their best memes. Community votes.' },
        { month: 'June', name: '🌀 Cursed Crossover Week', desc: 'Memes combining unexpected universes.' },
        { month: 'September', name: '📜 The Deep Lore Meme Drop', desc: 'Memes requiring actual series knowledge.' },
        { month: 'December', name: '🏅 Meme of the Year — Hall of Fame', desc: 'Greatest memes enshrined forever.' },
      ],
    },
    {
      name: 'Meta Library',
      subtitle: 'Light Novels · Comics · Manga',
      icon: '📚',
      category: 'library',
      officials: [
        { role: 'Head Librarian', name: '[ Name ]' },
        { role: 'Manga Curator', name: '[ Name ]' },
        { role: 'Novel Archivist', name: '[ Name ]' },
        { role: 'Comics Keeper', name: '[ Name ]' },
      ],
      events: [
        { month: 'April', name: '📖 Library Inauguration — First Drop', desc: 'First official curated list released.' },
        { month: 'June', name: '🖊️ Manga vs Light Novel Debate', desc: 'Which format tells the better story?' },
        { month: 'August', name: '🌙 Summer Reading Challenge', desc: 'Members commit to finishing selected titles.' },
        { month: 'October', name: '👁️ Dark Manga Spotlight', desc: 'Psychological manga and horror novels.' },
        { month: 'December', name: '📕 Title of the Year — Library Awards', desc: 'Best manga, best light novel, best comic.' },
      ],
    },
    {
      name: 'BurstKnight',
      subtitle: 'Role Playing · RPG · Character Builds',
      icon: '⚔️',
      category: 'burst',
      officials: [
        { role: 'Grand Master', name: '[ Name ]' },
        { role: 'Campaign Architect', name: '[ Name ]' },
        { role: 'Lore Forger', name: '[ Name ]' },
        { role: 'Battle Referee', name: '[ Name ]' },
        { role: 'New Recruit Handler', name: '[ Name ]' },
      ],
      events: [
        { month: 'April', name: '🛡️ Character Creation Week — Season I', desc: 'New members build their characters.' },
        { month: 'May', name: '🌋 First Campaign — The Justice League Arc', desc: 'First major RPG campaign launches.' },
        { month: 'July', name: '⚡ The Mid-Year Raid', desc: 'Massive group raid event with alliances.' },
        { month: 'September', name: '🩸 Cursed Domain Campaign — JJK Arc', desc: 'Darkest campaign yet with impossible choices.' },
        { month: 'November', name: '🏴‍☠️ The Grand Voyage Campaign', desc: 'Sea-bound adventure inspired by One Piece.' },
        { month: 'December', name: '🌟 Season I Final — The Infinite War', desc: 'Everything converges in one climactic battle.' },
      ],
    },
    {
      name: 'Tartarus',
      subtitle: 'The Abyss · Dark Content · The Unspoken',
      icon: '🕳️',
      category: 'tartarus',
      officials: [
        { role: 'The Warden', name: '[ Name ]' },
        { role: 'Shadow Curator', name: '[ Name ]' },
        { role: 'Abyss Keeper', name: '[ Name ]' },
        { role: 'Gate Sentinel', name: '[ Name ]' },
      ],
      events: [
        { month: 'April', name: '🔒 Tartarus Initiation — The Descent', desc: 'New members formally initiated into Tartarus.' },
        { month: 'June', name: '☠️ Villain Studies Vol. I', desc: 'Analysis of the greatest villains in fiction.' },
        { month: 'August', name: '🌑 The Blackout Drop', desc: 'Most unsettling content picks of the year.' },
        { month: 'October', name: '👁️ Dark Convergence Night — All Community', desc: 'The entire community descends for one night.' },
        { month: 'December', name: '🕯️ The Abyss Remembers — Year-End Ritual', desc: 'Reflecting on the darkest stories told.' },
      ],
    },
    {
      name: 'Ragnarök',
      subtitle: 'Tournaments · Debates · The Arena',
      icon: '⚡',
      category: 'ragnarok',
      officials: [
        { role: 'Arena Lord', name: '[ Name ]' },
        { role: 'Chief Judge', name: '[ Name ]' },
        { role: 'Tournament Architect', name: '[ Name ]' },
        { role: 'Debate Warden', name: '[ Name ]' },
        { role: 'Record Keeper', name: '[ Name ]' },
      ],
      events: [
        { month: 'April', name: '🥊 Arena Opening — The First Call', desc: 'Rules established. First challengers registered.' },
        { month: 'May', name: '⚔️ Who Wins? — Power Level Debates', desc: 'Classic versus battles with official judging.' },
        { month: 'July', name: '🏟️ Mid-Year Grand Tournament', desc: 'Full bracket tournament with first champion crowned.' },
        { month: 'September', name: '🌍 Community-Wide Open Tournament', desc: 'All groups send their champions into the arena.' },
        { month: 'November', name: '🔥 The Grudge Matches', desc: 'Unresolved debates settled once and for all.' },
        { month: 'December', name: '👑 Ragnarök Year-End Championship', desc: 'The final and greatest tournament of the year.' },
      ],
    },
  ];

  const getCategoryColor = (category) => {
    const colors = {
      anime: { border: 'border-cyan-500/30', bg: 'bg-cyan-500/10', text: 'text-cyan-400', accent: 'from-cyan-500 to-purple-500' },
      western: { border: 'border-yellow-500/30', bg: 'bg-yellow-500/10', text: 'text-yellow-400', accent: 'from-yellow-500 to-amber-600' },
      meme: { border: 'border-purple-500/30', bg: 'bg-purple-500/10', text: 'text-purple-400', accent: 'from-purple-500 to-pink-500' },
      library: { border: 'border-teal-500/30', bg: 'bg-teal-500/10', text: 'text-teal-400', accent: 'from-teal-500 to-cyan-500' },
      burst: { border: 'border-orange-500/30', bg: 'bg-orange-500/10', text: 'text-orange-400', accent: 'from-orange-500 to-red-500' },
      tartarus: { border: 'border-red-600/30', bg: 'bg-red-600/10', text: 'text-red-500', accent: 'from-red-600 to-rose-700' },
      ragnarok: { border: 'border-yellow-400/30', bg: 'bg-yellow-400/10', text: 'text-yellow-300', accent: 'from-yellow-400 to-amber-500' },
      east: { border: 'border-cyan-500/30', bg: 'bg-cyan-500/10', text: 'text-cyan-400', accent: 'from-cyan-500 to-blue-500' },
      west: { border: 'border-yellow-500/30', bg: 'bg-yellow-500/10', text: 'text-yellow-400', accent: 'from-yellow-500 to-amber-600' },
    };
    return colors[category] || colors.western;
  };

  return (
    <>
      <SEO
        title="Community Master Calendar - ANIMAC Metaverse"
        description="The official community calendar for ANIMAC Metaverse - Monthly themes, events, and group profiles."
        url="/community/calendar"
        type="website"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-900 to-gray-900 text-white p-6 md:p-12">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="text-xs tracking-[0.4em] text-yellow-500 uppercase mb-4">✦ Animac Metaverse Official Document ✦</div>
          <h1 className="text-3xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-yellow-300 via-yellow-500 to-yellow-600 bg-clip-text text-transparent">
            Community Master Calendar
          </h1>
          <div className="text-gray-400 tracking-[0.2em] font-light mb-6">Districts · Gates · Guilds · Arenas</div>
          <div className="inline-block px-6 py-2 border border-yellow-700/50 text-yellow-500 text-xs tracking-[0.3em] bg-yellow-900/10">
            APRIL — DECEMBER 2025
          </div>
        </motion.div>

        {/* Monthly Themes */}
        <section className="max-w-6xl mx-auto mb-16">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-center mb-8"
          >
            <h2 className="text-xl md:text-2xl font-bold text-yellow-500 tracking-[0.15em] mb-2">Monthly Themes</h2>
            <p className="text-gray-500 text-sm">Each month, the entire community gathers under one theme — alternating between Anime & Western universes</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {monthlyThemes.map((theme, index) => {
              const colors = theme.category === 'anime' 
                ? { border: 'border-cyan-500/30', top: 'from-cyan-500 to-purple-400', type: 'text-cyan-400' }
                : { border: 'border-yellow-500/30', top: 'from-yellow-500 to-red-500', type: 'text-yellow-400' };
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`border ${colors.border} bg-gray-900/80 p-5 relative overflow-hidden`}
                >
                  <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${colors.top}`} />
                  <div className="text-xs tracking-[0.3em] text-gray-500 uppercase mb-2">{theme.month}</div>
                  <div className={`text-xs tracking-[0.2em] uppercase mb-2 font-semibold ${colors.type}`}>{theme.type}</div>
                  <div className="font-bold text-lg mb-2 text-white">{theme.name}</div>
                  <div className="text-sm text-gray-400 italic leading-relaxed">{theme.tagline}</div>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Community Events */}
        <section className="max-w-4xl mx-auto mb-16">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-center mb-8"
          >
            <h2 className="text-xl md:text-2xl font-bold text-yellow-500 tracking-[0.15em] mb-2">Community-Wide Events</h2>
            <p className="text-gray-500 text-sm">Events open to every member across all groups — mark your calendars</p>
          </motion.div>

          <div className="relative pl-8 border-l border-yellow-700/30 space-y-6">
            {communityEvents.map((event, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="relative"
              >
                <div className="absolute -left-8 top-4 text-yellow-500 text-xs">✦</div>
                <div className="border border-yellow-700/20 bg-gray-900/60 p-5 hover:border-yellow-500/40 transition-colors">
                  <div className="text-xs tracking-[0.35em] text-yellow-500 uppercase mb-2">{event.month}</div>
                  <div className="font-bold text-lg mb-2 text-white">{event.name}</div>
                  <div className="text-sm text-gray-400 mb-3">{event.desc}</div>
                  <span className={`inline-block px-3 py-1 text-xs tracking-[0.2em] uppercase border ${
                    event.tagClass === 'tag-grand' ? 'border-red-500/50 text-red-400' :
                    event.tagClass === 'tag-special' ? 'border-cyan-500/50 text-cyan-400' :
                    'border-yellow-500/50 text-yellow-400'
                  }`}>
                    {event.tag}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Group Cards */}
        <section className="max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-center mb-8"
          >
            <h2 className="text-xl md:text-2xl font-bold text-yellow-500 tracking-[0.15em] mb-2">The Seven Gates — Group Profiles</h2>
            <p className="text-gray-500 text-sm">Each group, its officials, and its exclusive events from April to December</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {groups.map((group, index) => {
              const colors = getCategoryColor(group.category);
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`border ${colors.border} bg-gray-900/80 overflow-hidden`}
                >
                  <div className={`p-5 bg-gradient-to-r ${colors.bg} border-b ${colors.border}`}>
                    <div className="text-2xl mb-2">{group.icon}</div>
                    <div className={`font-bold text-xl ${colors.text}`}>{group.name}</div>
                    <div className="text-xs tracking-[0.15em] uppercase text-gray-500">{group.subtitle}</div>
                  </div>
                  <div className="p-5">
                    <div className="mb-4">
                      <div className="text-xs tracking-[0.3em] text-gray-500 uppercase mb-3 opacity-60">✦ Group Officials</div>
                      {group.officials.map((official, i) => (
                        <div key={i} className="flex justify-between py-1 border-b border-gray-800">
                          <span className={`text-sm font-semibold ${colors.text}`}>{official.role}</span>
                          <span className="text-sm text-gray-500 italic">{official.name}</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <div className="text-xs tracking-[0.3em] text-gray-500 uppercase mb-3 opacity-60">✦ Exclusive Events</div>
                      {group.events.map((event, i) => (
                        <div key={i} className={`py-2 border-l-2 ${colors.border} pl-3 mb-2`}>
                          <div className="text-xs text-gray-500 uppercase mb-1">{event.month}</div>
                          <div className="font-semibold text-sm text-white mb-1">{event.name}</div>
                          <div className="text-xs text-gray-400">{event.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Footer */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          className="text-center mt-16 pt-8 border-t border-yellow-700/20"
        >
          <div className="text-yellow-500 font-bold text-lg mb-2">✦ ANIMAC METAVERSE ✦</div>
          <div className="text-gray-500 text-sm tracking-[0.2em]">Building Your World · One Screen At A Time · 2025</div>
        </motion.div>
      </div>
    </>
  );
};

export default CommunityCalendar;